import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import {
  setIsFormComplete,
  setIsInputChecked,
  setUserData,
} from "../../redux/globalFunctions";

import translations from "../../en.json";
import translationsNo from "../../no.json";
import {
  addMultipleAnswers,
  deleteAnswer,
  setAnswers,
} from "../../redux/answers";
import { deleteQuestion, setQuestion } from "../../redux/answeredQuestions";

export function Question({ question, options, customFieldId, questionId }) {
  // Variables for redux state.
  const { level, quizType, userData, language } = useSelector(
    (state) => state.global
  );
  const { answers } = useSelector((state) => state.answers);
  const { questions } = useSelector((state) => state.answeredQuestions);

  // Helper variables.
  const [value, setValue] = useState("");
  const textareaRef = useRef(null);
  const dispatch = useDispatch();
  const multipleAnswersQIDs = [1, 2, 9];

  const termsAndConditionsLink = `https://avilo.no/${
    language === "en" ? "en/" : ""
  }evinity360/terms-and-conditions/`;

  const otherSelection = language === "en" ? "Custom" : "Tilpasset";
  const pageText = language === "en" ? translations : translationsNo;
  const [visibleOptions, setVisibleOptions] = useState(options);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSelected, setIsSelected] = useState(false);

  useEffect(() => {
    dispatch(setIsFormComplete(false));
  }, []);

  function setMultipleAnswers(option) {
    setIsSelected(true);
    textareaRef.current.focus();
    setValue((prevValue) => (prevValue ? `${prevValue}, ${option}` : option));
    setVisibleOptions((prevOptions) =>
      prevOptions.filter((item) => item !== option)
    );

    dispatch(
      addMultipleAnswers({ level, question, answer: option, questionId })
    );
    dispatch(setQuestion({ questionId }));
  }

  function setAnswer(option) {
    setIsSelected(true);
    textareaRef.current.focus();
    setValue(option);

    setVisibleOptions((prevOptions) => {
      const updatedOptions = [...prevOptions];
      if (selectedOption) {
        updatedOptions.push(selectedOption);
      }
      return updatedOptions.filter((item) => item !== option);
    });
    setSelectedOption(option);
    dispatch(setAnswers({ level, question, answer: option, questionId }));
    dispatch(setQuestion({ questionId }));
  }

  function handleTextareaChange(e) {
    const textVal = e.target.value;
    setValue(textVal);

    if (textVal === "") {
      dispatch(deleteAnswer({ level, questionId }));
      dispatch(deleteQuestion({ questionId }));
      setVisibleOptions(options);
      setSelectedOption(null);
      return;
    } else {
      dispatch(setAnswers({ level, question, answer: textVal, questionId }));
      dispatch(setQuestion({ questionId }));
    }
  }

  const levelsMap = {
    0: [0, 1, 2, 3],
    1: [4, 5],
    2: [6, 7],
    3: [8, 9],
    4: [10, 11],
    5: [12, 13],
    6: [14, 15],
  };

  // Check if the current level has the correct questions answered and enable the next button.
  useEffect(() => {
    if (quizType === "long" && levelsMap[level]) {
      const allQuestionsAnswered = levelsMap[level].every((item) =>
        questions.hasOwnProperty(item)
      );
      dispatch(setIsInputChecked(allQuestionsAnswered));
    }
  }, [questions, quizType, level]);

  useEffect(() => {
    setVisibleOptions(options);
    setValue("");
  }, [level, options]);

  function handleTextareaClick() {
    setIsSelected(true);
  }

  function handleTextareaBlur() {
    setTimeout(() => {
      setIsSelected(false);
    }, 100);
  }

  const isValidEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  // If email or fullname is an empty string, then disable the next button.
  useEffect(() => {
    const isEmailValid = isValidEmail(userData?.email);
    const isFormComplete = isEmailValid && userData?.fullname;
    const isTargetLevel =
      (quizType === "long" && level === 7) ||
      (quizType === "short" && level === 5);

    if (isTargetLevel) {
      dispatch(setIsInputChecked(isFormComplete));
    }
  }, [userData?.email, userData?.fullname]);

  function handleEmailChange(e) {
    const email = e.target.value;
    dispatch(
      setUserData({
        email,
        fullname: userData?.fullname,
      })
    );
  }

  return (
    <div className="question">
      {(quizType === "long" && level === 7) ||
      (quizType === "short" && level === 5) ? (
        <>
          <div className="emailInput" style={{ marginBottom: "20px" }}>
            <label htmlFor="fullname">{pageText.fullNameField.text}</label>
            <input
              type="text"
              id="fullname"
              value={userData?.fullname}
              onChange={(e) =>
                dispatch(
                  setUserData({
                    fullname: e.target.value,
                    email: userData?.email,
                  })
                )
              }
            />
          </div>
          <div className="emailInput">
            <label htmlFor="email">{question}</label>
            <input
              type="email"
              id="email"
              value={userData?.email}
              onChange={handleEmailChange}
            />
          </div>
          <div className="termsAndConditions">
            {pageText.submitAgreement.text1}{" "}
            <a href={termsAndConditionsLink} target="_blank">
              {pageText.submitAgreement.text2}
            </a>
          </div>
        </>
      ) : (
        <>
          <p>{question}</p>
          <div className="radio-group">
            {visibleOptions.map((option, index) =>
              option === otherSelection ? (
                <div className="customElementContainer" key={index}>
                  <div className="input-container">
                    <textarea
                      ref={textareaRef}
                      id={`${questionId}`}
                      name={`customField-${questionId}`}
                      className={isSelected ? "focused" : "notFocused"}
                      placeholder={pageText.customAnswer.placeholder}
                      value={
                        answers[level]?.[questionId]?.answer !== undefined
                          ? answers[level][questionId].answer
                          : value
                      }
                      onChange={(e) => handleTextareaChange(e, option)}
                      onClick={handleTextareaClick}
                      onBlur={handleTextareaBlur}
                    ></textarea>
                  </div>
                </div>
              ) : (
                <li
                  className="suggestion-chip"
                  id={option}
                  onMouseDown={(e) => e.preventDefault()}
                  key={`${customFieldId}-option-${questionId + index}`}
                  onClick={() =>
                    multipleAnswersQIDs.includes(questionId)
                      ? setMultipleAnswers(option)
                      : setAnswer(option)
                  }
                >
                  {option}
                </li>
              )
            )}
          </div>
        </>
      )}
    </div>
  );
}
